import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { withRouterProps } from '../../utils/with-router-props'
import { Link } from 'react-router-dom'
import { fetchApiDataIfNeeded, callApi } from '../../../actions/api'
import { apiResourceEndpoint, buildRoutePath, routes } from '../../../constants/routes'
import SearchComplete from '../../utils/search-complete'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { createSelector } from '@reduxjs/toolkit'
import { startCase } from 'lodash'
import SHSForm from '../../utils/shs-form'
import InputElement from '../../utils/input'


const selectInputGroup = (state, groupId) => state.adminInputGroups.find(g => g.id === Number(groupId))
const selectQuestions = state => state.adminQuestions
const selectGroupQuestions = (state, groupId) => state.adminGroupQuestions.filter(q => q.groupId === Number(groupId))

const makeAdminQuestions = createSelector(
    [selectQuestions, selectGroupQuestions],
    (adminQuestions, groupQuestions) => {
        if (!adminQuestions || !groupQuestions) {
            return []
        }

        // Create a map of questionId to displayOrder
        const orderMap = {}
        groupQuestions.forEach(gq => {
            orderMap[gq.questionId] = gq.displayOrder
        })

        const questionIds = groupQuestions.map(gq => gq.questionId)
        return adminQuestions.filter(question => questionIds.includes(question.id))
            .sort((a, b) => {
                // Sort by displayOrder from groupQuestions
                return (orderMap[a.id] || 0) - (orderMap[b.id] || 0)
            })
    })

const mapStateToProps = (state, ownProps) => {
    const groupId = ownProps.params.groupId
    const adminInputGroup = selectInputGroup(state, groupId)
    const adminGroupQuestions = selectGroupQuestions(state, groupId)
    const adminQuestions = makeAdminQuestions(state, groupId)
    return {
        adminInputGroup,
        adminQuestions,
        adminGroupQuestions
    }
}

const AdminTemplateGroupView = ({ dispatch, navigate, params, adminInputGroup, adminQuestions, adminGroupQuestions }) => {
    const [inputOpen, setInputOpen] = useState(false)
    const [draggedQuestion, setDraggedQuestion] = useState(null)

    useEffect(() => {
        dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('admin/input-groups', 'VIEW', params.groupId), {}))
    }, [dispatch, params.groupId])


    const renderQuestionRow = (question, index) => {
        const handleDragStart = (e) => {
            setDraggedQuestion(question)
            e.dataTransfer.effectAllowed = 'move'
            e.currentTarget.style.opacity = '0.4'
        }

        const handleDragEnd = (e) => {
            e.currentTarget.style.opacity = '1'
            setDraggedQuestion(null)
        }

        const handleDragOver = (e) => {
            if (e.preventDefault) {
                e.preventDefault()
            }
            e.dataTransfer.dropEffect = 'move'
            return false
        }

        const handleDragEnter = (e) => {
            e.currentTarget.classList.add('drag-over')
        }

        const handleDragLeave = (e) => {
            e.currentTarget.classList.remove('drag-over')
        }

        const handleDrop = (e) => {
            if (e.stopPropagation) {
                e.stopPropagation()
            }
            e.currentTarget.classList.remove('drag-over')

            if (draggedQuestion && draggedQuestion.id !== question.id) {
                handleReorderQuestions(draggedQuestion, question)
            }

            return false
        }

        return (
            <tr
                key={question.id}
                draggable="true"
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <td className="index">
                    <FontAwesomeIcon icon="grip-vertical" />
                    {index}
                </td>
                <td className="main">{question.label}</td>
                <td className="main">{startCase(question?._computed?.typeValue ? question._computed.typeValue.toLowerCase() : '')}</td>
                <td className="main">
                    <Link className="btn blue small" to={buildRoutePath(routes.adminQuestionView.path, { groupId: params.groupId, questionId: question.id })}>
                        Edit
                    </Link>
                </td>
                <td className="main">
                    <Link onClick={() => handleRemoveQuestion(question)}>Remove</Link>
                </td>
            </tr>
        )
    }

    const renderQuestions = () => {
        if (!adminQuestions.length) {
            return <p>No questions added to this group yet.</p>
        }
        let allQuestions = []
        adminQuestions.forEach((question, index) => {
            allQuestions.push(renderQuestionRow(question, index + 1))
        })
        return (
            <table className="table">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Label</th>
                        <th>Type</th>
                        <th>&nbsp;</th>
                        <th>&nbsp;</th>
                    </tr>
                </thead>
                <tbody>
                    {allQuestions}
                </tbody>
            </table>
        )
    }

    const handleDelete = () => {
        const confirm = window.confirm('Are you sure you want to delete this group?')
        if (confirm) {
            dispatch(callApi(apiResourceEndpoint('admin/input-groups', 'DELETE', params.groupId), {}, -1))
                .then(() => {
                    navigate(buildRoutePath(routes.adminTemplateGroupIndex.path))
                })
        }
    }

    const handleAddQuestion = (question) => {
        dispatch(callApi(apiResourceEndpoint('admin/input-groups', 'UPDATE', params.groupId, 'add-question'), { questionId: question.id }, -1))
            .then(() => {
                dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('admin/input-groups', 'VIEW', params.groupId)))
            })
    }

    const handleRemoveQuestion = (question) => {
        const isConfirmed = window.confirm('Are you sure you want to remove this question from the group?')
        if (isConfirmed) {
            dispatch(callApi(apiResourceEndpoint('admin/input-groups', 'DELETE', params.groupId, `remove-question/${question.id}`), {}, -1))
                .then(response => {
                    if (response === 'SUCCESS') {
                        dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('admin/group-questions', 'LIST'), { filter: [`group_id|${params.groupId}`] }))
                        dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('admin/input-groups', 'VIEW', params.groupId)))
                    }
                })
        }
    }

    const handleReorderQuestions = (draggedQuestionItem, targetQuestion) => {
        // Get current display orders from adminGroupQuestions
        const orderMap = {}
        adminGroupQuestions.forEach(gq => {
            orderMap[gq.questionId] = gq.displayOrder
        })

        let oldPosition = Number(orderMap[draggedQuestionItem.id])
        let newPosition = Number(orderMap[targetQuestion.id])

        // Fallback to rendered order if displayOrder values are duplicated or missing.
        if (!oldPosition || !newPosition || oldPosition === newPosition) {
            oldPosition = adminQuestions.findIndex(question => question.id === draggedQuestionItem.id) + 1
            newPosition = adminQuestions.findIndex(question => question.id === targetQuestion.id) + 1
        }

        if (!oldPosition || !newPosition || oldPosition === newPosition) {
            return
        }

        // Call API with just the moved question and its new position
        dispatch(callApi(
            apiResourceEndpoint('admin/input-groups', 'UPDATE', params.groupId, 'update-question-order'),
            {
                questionId: draggedQuestionItem.id,
                oldPosition,
                newPosition
            },
            -1
        ))
    }

    const renderQuestionSearch = () => {
        const questionIds = adminGroupQuestions?.map(gq => gq.questionId) || []
        let excludeFilter = []
        if (questionIds.length > 0) {
            excludeFilter = [`id|${questionIds.join(',')}|WHERE_NOT_IN`]
        }
        return (
            <div className="search-complete">
                <SearchComplete
                    key={adminQuestions.length}
                    label="Search Questions"
                    searchEndpoint="admin/questions"
                    renderTemplate={(question) => question.label}
                    excludeFilter={excludeFilter}
                    onHandleSelect={handleAddQuestion}
                    allowCreation={false}
                    placeholder="Search and Add Questions to Group" />
            </div>
        )
    }

    const submitChangeStatus = (formData, formId) => {
        dispatch(callApi(apiResourceEndpoint('admin/input-groups', 'UPDATE', params.groupId), formData, formId))
    }

    if (!adminInputGroup) {
        return <div>Loading...</div>
    }

    return (
        <div className="admin-dashboard">
            <div className="header">
                <div className="container">
                    <div className="header-details">
                        { !inputOpen &&
                            <h1 className="clickable" onClick={() => setInputOpen(!inputOpen)}>{adminInputGroup.name}</h1>
                        }
                        { inputOpen &&
                            <SHSForm showCancel={true} cancelAction={() => setInputOpen(!inputOpen)} formClass="inline-form" submitFX={submitChangeStatus} successFX={() => setInputOpen(!inputOpen)}>
                                <InputElement
                                    type="text"
                                    id="name"
                                    default={adminInputGroup.name} />
                            </SHSForm>
                        }
                        <p><small>Click title above to edit</small></p>
                    </div>
                </div>
            </div>
            <div className="bg-dark content-wrapper">
                <div className="dashboard-wrapper admin-box">
                    <div className="container">
                        {renderQuestions()}
                    </div>
                    <div className="container">
                        <div className="content-box">
                            <h3>Add Questions to Group</h3>
                            {renderQuestionSearch()}
                        </div>
                    </div>
                    <div className="container">
                        <div className="content-box delete-wrapper">
                            <div className="btn small red" onClick={handleDelete}>
                                DELETE GROUP <FontAwesomeIcon icon="trash-alt" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default withRouterProps(connect(mapStateToProps)(AdminTemplateGroupView))
